import { useState, useEffect } from "react";

import { useTenantSettings } from "@nestoca/multi-tenant";
import {
  Button,
  Flex,
  Typography,
  Skeleton,
  useBreakpointValue,
} from "@nestoca/ui";
import { useGetAccount } from "@shared/api/hooks/account";
import { useServicingAssets } from "@shared/api/hooks/mortgages-servicing";
import { DatadogMask } from "@shared/datadog";
import { getApplicantName } from "@shared/utils";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation, Trans } from "next-i18next";
import { PiArrowRight } from "react-icons/pi";

import styles from "./home-page.module.scss";

export const HomeHeader = ({ className }: { className?: string }) => {
  const [isClient, setIsClient] = useState(false);
  const { t } = useTranslation("common");
  const { t: tApplications } = useTranslation("applications");
  const isMobile = useBreakpointValue({ default: true, md: false });
  const { data: account, isPending: isLoadingAccount } = useGetAccount();
  const { data: servicingAssets } = useServicingAssets();
  const { enableLinkToPortal } = useTenantSettings();
  const { locale } = useRouter();

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) return null;

  const applicantName = getApplicantName(account, t("unknownName"));
  const showPortalButton =
    !!servicingAssets?.length && enableLinkToPortal.value;

  return (
    <Flex className={clsx(styles["home-header"], className)} direction="column">
      {isMobile && (
        <Typography size={3} weight={7}>
          {t("portfolio")}
        </Typography>
      )}
      {isLoadingAccount && (
        <Skeleton containerClassName={styles.home__applicant} />
      )}
      <Flex
        align={isMobile ? "start" : "center"}
        justify={isMobile ? "between" : "center"}
        direction="column"
        gap={isMobile ? 5 : 3}
      >
        <Typography
          weight={7}
          className={styles["home-header__text"]}
          e2ePrefix="account-header"
        >
          <Trans
            t={t}
            i18nKey={isMobile ? "account.header" : "account.headerLong"}
            values={{
              name: applicantName,
            }}
            components={[
              <DatadogMask
                key={0}
                className={styles["home-name"]}
                data-dd-action-name="applicant name"
              >
                {applicantName}
              </DatadogMask>,
            ]}
          />
        </Typography>
        {showPortalButton && (
          <Button
            as="a"
            href={`/portal/${locale}`}
            variant="primary"
            size="medium"
            rightIcon={<PiArrowRight size={13} />}
          >
            {tApplications("myPortal")}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
